import { useEffect, useState } from 'react';
import { holidayAPI, guestAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Calendar, Package, Trash2, Loader2, CalendarDays, Gift, Building } from 'lucide-react';

const holidayTypes = [
  { value: 'national', label: 'National Holiday', color: 'bg-red-100 text-red-800' },
  { value: 'regional', label: 'Regional Holiday', color: 'bg-orange-100 text-orange-800' },
  { value: 'company', label: 'Company Holiday', color: 'bg-blue-100 text-blue-800' },
  { value: 'optional', label: 'Optional Holiday', color: 'bg-purple-100 text-purple-800' },
];

const currentYear = new Date().getFullYear();
const years = [currentYear - 1, currentYear, currentYear + 1];

export default function HolidaysPage() {
  const { user, isAdmin, isHR, isGuest } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [activeTab, setActiveTab] = useState('holidays');
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'company',
    description: '',
  });

  const canManageHolidays = isAdmin || isHR;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [holidaysRes, assetsRes] = await Promise.all([
        holidayAPI.getAll({ year: selectedYear }),
        guestAPI.getAvailableAssets(),
      ]);
      setHolidays(holidaysRes.data);
      setAvailableAssets(assetsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await holidayAPI.create({
        ...formData,
        date: new Date(formData.date).toISOString(),
      });
      toast.success('Holiday added successfully');
      setDialogOpen(false);
      setFormData({ name: '', date: '', type: 'company', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return;
    try {
      await holidayAPI.delete(id);
      toast.success('Holiday deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const getHolidayTypeConfig = (type) => holidayTypes.find(t => t.value === type) || holidayTypes[2];

  // Fix: Compare dates without time component by setting hours to 0
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const upcomingHolidays = holidays.filter(h => {
    const holidayDate = new Date(h.date);
    holidayDate.setHours(0, 0, 0, 0);
    return holidayDate >= today;
  });
  const pastHolidays = holidays.filter(h => {
    const holidayDate = new Date(h.date);
    holidayDate.setHours(0, 0, 0, 0);
    return holidayDate < today;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Company Information</h1>
          <p className="text-muted-foreground mt-1">
            {isGuest ? 'View company holidays and available assets' : 'Manage holidays and view available assets'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-24">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canManageHolidays && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="add-holiday-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Holiday
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Holiday</DialogTitle>
                  <DialogDescription>Add a holiday to the company calendar</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Holiday Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Diwali"
                      required
                      data-testid="holiday-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="holiday-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                      <SelectTrigger data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {holidayTypes.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description (Optional)</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Additional notes..."
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={submitting} data-testid="holiday-submit-btn">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Holiday'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-green-700">
              <CalendarDays className="w-4 h-4" />
              Total Holidays ({selectedYear})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-700">{holidays.length}</p>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
              <Gift className="w-4 h-4" />
              Upcoming Holidays
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-700">{upcomingHolidays.length}</p>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-purple-700">
              <Package className="w-4 h-4" />
              Available Assets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-purple-700">{availableAssets.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="holidays">
            <Calendar className="w-4 h-4 mr-2" /> Holiday Calendar
          </TabsTrigger>
          <TabsTrigger value="assets">
            <Package className="w-4 h-4 mr-2" /> Available Assets
          </TabsTrigger>
        </TabsList>

        <TabsContent value="holidays">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Holiday List - {selectedYear}</CardTitle>
              <CardDescription>Company holidays and observances</CardDescription>
            </CardHeader>
            <CardContent>
              {holidays.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No holidays found for {selectedYear}</p>
                  {canManageHolidays && <p className="text-sm mt-2">Click &quot;Add Holiday&quot; to add one</p>}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Holiday</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Day</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      {canManageHolidays && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {holidays.map((holiday) => {
                      const typeConfig = getHolidayTypeConfig(holiday.type);
                      const holidayDate = new Date(holiday.date);
                      const isPast = holidayDate < new Date();
                      return (
                        <TableRow key={holiday.id} className={isPast ? 'opacity-60' : ''}>
                          <TableCell className="font-medium">{holiday.name}</TableCell>
                          <TableCell>{formatDate(holiday.date)}</TableCell>
                          <TableCell>
                            {holidayDate.toLocaleDateString('en-US', { weekday: 'long' })}
                          </TableCell>
                          <TableCell>
                            <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {holiday.description || '-'}
                          </TableCell>
                          {canManageHolidays && (
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(holiday.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assets">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                Available Assets
              </CardTitle>
              <CardDescription>Assets currently available for allocation</CardDescription>
            </CardHeader>
            <CardContent>
              {availableAssets.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No available assets at the moment</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset Name</TableHead>
                      <TableHead>Tag ID</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableAssets.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell className="font-medium">{asset.name}</TableCell>
                        <TableCell>
                          <code className="bg-muted px-2 py-1 rounded text-sm">{asset.tag_id}</code>
                        </TableCell>
                        <TableCell>{asset.category}</TableCell>
                        <TableCell>{asset.location || '-'}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">Available</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
